import { Injectable } from '@angular/core';
import { Observable, map, of, switchMap } from 'rxjs';
import { Affiliation, AffiliationRole } from 'libs/affiliation/affiliation.model';
import { AffiliationService } from 'libs/affiliation/affiliation.service';
import { AdminGrantService } from 'libs/affiliation/admin-grant.service';
import { projectAvailableContexts, projectLegacyRole } from 'libs/user/legacy-role-projection';
import { Context, ContextService } from 'libs/context/context.service';

/**
 * Point unique de décision d'autorisation — lit affiliations/adminGrants,
 * jamais users/{id}.role. Remplace la décision aujourd'hui dupliquée dans
 * auth.guard.ts, app.component.ts, avatar.page.ts (x2), login.component.ts.
 *
 * Volontairement non câblé dans un guard tant que la Tranche A du sprint 2
 * (rapport-link-up/PLAN_SPRINTS_ET_AUDIT_ROLES.md) n'est pas basculée.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  constructor(
    private affiliationService: AffiliationService,
    private adminGrantService: AdminGrantService,
    private contextService: ContextService,
  ) {}

  getActiveAffiliations(identityId: string): Observable<Affiliation[]> {
    return this.affiliationService.getActiveAffiliations(identityId);
  }

  hasPedagogicalRole(identityId: string, trainingId: string, role: AffiliationRole): Observable<boolean> {
    return this.affiliationService.hasActiveRole(identityId, trainingId, role);
  }

  hasAnyPedagogicalRole(identityId: string, trainingId: string, roles: AffiliationRole[]): Observable<boolean> {
    return this.affiliationService
      .getAffiliation(identityId, trainingId)
      .pipe(map((affiliation) => !!affiliation && affiliation.status === 'active' && roles.includes(affiliation.role)));
  }

  isCenterAdmin(identityId: string, trainingCenterId: string): Observable<boolean> {
    return this.adminGrantService.isCenterAdmin(identityId, trainingCenterId);
  }

  isNetworkAdmin(identityId: string, networkId: string): Observable<boolean> {
    return this.adminGrantService.isNetworkAdmin(identityId, networkId);
  }

  /** Admin de quoi que ce soit (centre ou réseau), sans périmètre précis. */
  hasAnyAdminGrant(identityId: string): Observable<boolean> {
    return this.adminGrantService.hasAnyActiveGrant(identityId);
  }

  /** Admin réseau d'au moins un réseau, sans préciser lequel. */
  hasAnyNetworkGrant(identityId: string): Observable<boolean> {
    return this.adminGrantService.hasAnyNetworkGrant(identityId);
  }

  /**
   * Rôle hérité (apprentice/tutor/manager/alumni) dérivé des affiliations
   * plutôt que de users/{id}.role. Regarde l'ensemble complet (actives +
   * terminées) : un alumni pur n'a plus d'affiliation active, mais reste
   * détectable par une affiliation apprentice terminée. `null` si aucune
   * affiliation ne permet d'en choisir un (ex. les 30 apprentis orphelins
   * mesurés au sprint 0).
   *
   * Tant que le vrai sélecteur de contexte n'existe pas (sprint 3), c'est le
   * meilleur candidat disponible pour une décision "sans formation en
   * contexte" — ce que sont la quasi-totalité des routes aujourd'hui.
   */
  resolveLegacyRole(identityId: string): Observable<string | null> {
    return this.affiliationService.getAllAffiliations(identityId).pipe(
      map((affiliations) => projectLegacyRole(affiliations).role ?? null),
    );
  }

  resolveDefaultRoute(identityId: string): Observable<string | null> {
    return this.resolveLegacyRole(identityId).pipe(map((role) => (role ? `${role}/home` : null)));
  }

  /**
   * Sélecteur de contexte (Sprint 3) — ensemble DISTINCT des rôles tenus
   * (contrairement à `resolveLegacyRole`, qui n'en retient qu'un par
   * priorité). `null` : identité sans aucune affiliation (orphelins mesurés
   * au sprint 0) — à distinguer de `[]`, qui ne peut pas survenir ici mais
   * documente l'intention.
   */
  getAvailableContexts(identityId: string): Observable<Context[]> {
    return this.affiliationService
      .getAllAffiliations(identityId)
      .pipe(map((affiliations) => projectAvailableContexts(affiliations) as Context[]));
  }

  /**
   * Formation → rôle, pour les affiliations actives — base du comptage
   * croisé des notifications par contexte
   * (`NotificationService.getNotificationCountsByRole`).
   */
  getActiveTrainingRoleMap(identityId: string): Observable<{ [trainingId: string]: string }> {
    return this.getActiveAffiliations(identityId).pipe(
      map((affiliations) =>
        affiliations.reduce(
          (map, affiliation) => {
            map[affiliation.trainingId] = affiliation.role;
            return map;
          },
          {} as { [trainingId: string]: string },
        ),
      ),
    );
  }

  /**
   * Formations où l'identité tient ACTIVEMENT ce rôle précis (Sprint 3) —
   * filtre `user.trainings` (union légataire, tout rôle confondu) à ce qui
   * est réellement pertinent pour un menu/accueil d'un rôle donné. Avant le
   * Sprint 3 chaque entrée de `user.trainings` correspondait forcément au
   * rôle courant (personne n'était multi-rôle) ; ce n'est plus vrai, et
   * plusieurs écrans (menus/accueils tuteur et stagiaire) plantent en
   * déréférençant des objets qui n'existent que pour LE rôle de l'écran.
   *
   * `legacyTrainings` sert de repli identique au comportement d'avant si
   * l'identité n'a encore aucune affiliation migrée (comptes de recette,
   * orphelins) — ne jamais régresser un compte faute de migration.
   */
  getActiveTrainingIdsForRole(
    identityId: string,
    role: AffiliationRole,
    legacyTrainings?: string[],
  ): Observable<string[]> {
    return this.getActiveTrainingRoleMap(identityId).pipe(
      map((trainingRoleMap) => {
        const entries = Object.entries(trainingRoleMap);
        if (entries.length === 0) {
          return legacyTrainings ?? [];
        }
        return entries.filter(([, r]) => r === role).map(([trainingId]) => trainingId);
      }),
    );
  }

  /**
   * Point d'entrée unique après connexion ou changement de fiche : où
   * envoyer l'identité. Remplace `${user.role}/home` utilisé jusqu'ici
   * directement dans `app.component.ts`/`avatar.page.ts`.
   *
   * - 0 contexte (orphelin) : `fallbackRole/home` si fourni (repli sur
   *   `users/{id}.role`), sinon `null`.
   * - 1 contexte : direct vers son accueil, aucun écran — comportement
   *   identique à aujourd'hui pour la quasi-totalité des comptes.
   * - 2+ contextes ET un choix déjà posé cette session (`ContextService`) :
   *   direct vers l'accueil du contexte choisi.
   * - 2+ contextes et rien choisi encore : vers l'écran de sélection.
   */
  resolveEntryRoute(identityId: string, fallbackRole?: string): Observable<string | null> {
    return this.getAvailableContexts(identityId).pipe(
      map((contexts) => {
        if (contexts.length === 0) {
          return fallbackRole ? `${fallbackRole}/home` : null;
        }
        if (contexts.length === 1) {
          return `${contexts[0]}/home`;
        }
        const selected = this.contextService.getContext();
        if (selected && contexts.includes(selected)) {
          return `${selected}/home`;
        }
        return 'context-selector';
      }),
    );
  }

  /**
   * Rôle effectif pour l'AUTORISATION de navigation (Sprint 3) — consommé
   * par `AuthGuard`. Distinct de `resolveLegacyRole` : celui-ci retombe
   * toujours sur un seul rôle par priorité (apprenti-actif > tuteur > ...),
   * ce qui bloquait injustement l'accès à `tutor/home` pour une identité
   * dont l'apprentissage est prioritaire mais qui a explicitement choisi le
   * contexte tuteur dans le sélecteur.
   *
   * - 0 contexte (orphelin) : repli sur `resolveLegacyRole`/`fallbackRole`.
   * - 1 contexte : lui, directement — aucune ambiguïté possible.
   * - 2+ contextes ET un choix posé cette session, encore valide : lui.
   * - 2+ contextes et rien choisi (ou choix devenu invalide) : `null` —
   *   signal pour `AuthGuard` de renvoyer vers le sélecteur plutôt que de
   *   deviner un rôle arbitraire.
   */
  resolveEffectiveRole(identityId: string, fallbackRole?: string): Observable<string | null> {
    return this.getAvailableContexts(identityId).pipe(
      switchMap((contexts) => {
        if (contexts.length === 0) {
          return this.resolveLegacyRole(identityId).pipe(map((role) => role ?? fallbackRole ?? null));
        }
        if (contexts.length === 1) {
          return of(contexts[0] as string);
        }
        const selected = this.contextService.getContext();
        if (selected && contexts.includes(selected)) {
          return of(selected as string);
        }
        return of(null);
      }),
    );
  }
}
